/**
 * Optimized Quote Cart Manager
 * Reduces event listeners and provides centralized cart management
 */

class QuoteCartManager {
  constructor() {
    this.cart = this.loadCart();
    this.initialize();
  }

  initialize() {
    // Single DOMContentLoaded listener
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onPageLoad());
    } else {
      this.onPageLoad();
    }
  }

  onPageLoad() {
    this.updateCartDisplay();
    this.setupDelegatedEventListeners();
  }

  loadCart() {
    try {
      const stored = sessionStorage.getItem('quoteCart');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to parse quote cart:', e);
      return [];
    }
  }

  saveCart() {
    sessionStorage.setItem('quoteCart', JSON.stringify(this.cart));
  }

  normalizeProductType(value) {
    return String(value || '').trim();
  }

  addToCart(product) {
    const existingIndex = this.cart.findIndex(item => item.variant_id === product.variant_id);
    
    if (existingIndex === -1) {
      this.cart.push({ ...product, quantity: product.quantity || 1 });
      this.saveCart();
      this.updateCartDisplay();
      
      window.dispatchEvent(new Event("quoteCartQuantityChanged"));
      return true;
    }
    return false; // Already exists
  }

  removeFromCart(variantId) {
    this.cart = this.cart.filter(item => item.variant_id !== variantId);
    this.saveCart();
    this.updateCartDisplay();
    window.dispatchEvent(new Event("quoteCartQuantityChanged"));
  }

  updateQuantity(variantId, quantity) {
    const item = this.cart.find(item => item.variant_id === variantId);
    if (item && quantity > 0) {
      item.quantity = quantity;
      this.saveCart();
      this.updateCartDisplay();
      
      window.dispatchEvent(new Event("quoteCartQuantityChanged"));
    }
  }

  showOrHideCartContent() {
    const emptyMessage = document.getElementById('quote-cart-empty-container');
    const quoteBody = document.getElementById('quote-page');

    if (emptyMessage && quoteBody) {
      const hasItems = this.cart.length > 0;
      
      if (hasItems) {
        emptyMessage.classList.add('hidden');
        quoteBody.classList.remove('hidden');
      } else {
        emptyMessage.classList.remove('hidden');
        quoteBody.classList.add('hidden');
      }
    }
  }

  updateCartDisplay() {
    this.showOrHideCartContent();
    this.renderCartItems();
    this.updateQuoteCartBadge();
    
    // Dispatch event for any remaining listeners
    window.dispatchEvent(new CustomEvent('quoteCartChanged', { 
      detail: { cart: this.cart } 
    }));
  }

  renderCartItems() {
    const displayEl = document.getElementById('quote-cart-display');
    if (!displayEl) return;

    if (!this.cart.length) {
      displayEl.innerHTML = '<p>No items in quote cart.</p>';
      this.updatePricingSummary();
      return;
    }

    const cartItemsHTML = this.cart.map((item, index) => {
      const itemHTML = this.renderCartItem(item);
      // Add divider after each item except the last one
      const divider = index < this.cart.length - 1 
        ? '<div style="height: 1px; background-color: #D1D1D1; margin: 25px 0;"></div>' 
        : '';
      return itemHTML + divider;
    }).join('');
    
    displayEl.innerHTML = cartItemsHTML;
    this.updatePricingSummary();
  }

  updatePricingSummary() {
    let cartTotal = 0;
    let hasPendingQuote = false;
    
    this.cart.forEach(item => {
      const unitPrice = parseFloat(item.price) / 100;
      if (item.price === 0 || unitPrice === 0) {
        hasPendingQuote = true;
      } else {
        cartTotal += unitPrice * item.quantity;
      }
    });

    const displayedCartTotal = hasPendingQuote ? "Pending Quote" : cartTotal.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
    
    // Update the pricing summary in quote-modal.liquid
    const subtotalEl = document.getElementById('quote-subtotal');
    if (subtotalEl) {
      subtotalEl.textContent = displayedCartTotal;
    }
    
    // For now, set shipping and taxes to Pending Quote. TODO add to total
    const shippingEl = document.getElementById('quote-shipping');
    const taxesEl = document.getElementById('quote-taxes');
    const totalEl = document.getElementById('quote-total');
    
    if (shippingEl) shippingEl.textContent = 'Pending Quote';
    if (taxesEl) taxesEl.textContent = 'Pending Quote';
    if (totalEl) totalEl.textContent = displayedCartTotal
  }

  updateQuoteCartBadge() {
    const totalQuantity = this.cart.reduce((total, item) => total + item.quantity, 0);
    const quoteNavItems = document.querySelectorAll('a.quote-request-nav-link, a[href*="quote-request"], a[href="/pages/quote-request"]');
    quoteNavItems.forEach(navItem => {
      let badge = navItem.querySelector('.quote-cart-badge');
      
      if (totalQuantity > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'quote-cart-badge';
          badge.style.cssText = `
            position: absolute;
            top: 6px;
            right: 8px;
            background: #10264D;
            color: #FFF;
            border-radius: 50%;
            min-width: 21px;
            height: 21px;
            font-size: calc(var(--font-body-scale) * 1.2rem);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
          `;
          
          if (getComputedStyle(navItem).position === 'static') {
            navItem.style.position = 'relative';
          }
          
          navItem.appendChild(badge);
        }
        badge.textContent = totalQuantity;
        badge.style.display = 'flex';
      } else {
        if (badge) {
          badge.style.display = 'none';
        }
      }
    });
  }

  escapeHtml(value) {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Allow only <sub> and <sup> in displayed titles. Everything else stays escaped.
  sanitizeTitleHtml(value) {
    const escaped = this.escapeHtml(value);
    return escaped
      .replace(/&lt;sub&gt;/gi, "<sub>")
      .replace(/&lt;\/sub&gt;/gi, "</sub>")
      .replace(/&lt;sup&gt;/gi, "<sup>")
      .replace(/&lt;\/sup&gt;/gi, "</sup>");
  }

  renderCartItem(item) {
    const isPendingQuote = item.price === 0;
    const totalDisplay = isPendingQuote
      ? "Pending Quote"
      : (item.price * item.quantity / 100).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });
    const safeTitle = this.escapeHtml(item.product_title);
    const displayTitle = this.sanitizeTitleHtml(item.product_title);
    const safeSku = this.escapeHtml(item.sku);

    return `
      <div class="quote-cart-item" data-variant-id="${item.variant_id}">
        <div class="quote-cart-product-container">
          <div class="quote-cart-product-item-description">
            <div class="quote-cart-product-image">
              <img src="${item.image}" alt="${safeTitle}">
            </div>
            <div class="quote-cart-product-description">
              <div>
                <h5 style="margin-bottom: 0;">${displayTitle}</h5>
                <p><strong>SKU:</strong> ${safeSku}</p>
              </div>
              <div class="quote-quantity-buttons">
                <button type="button" class="quote-qty-decrement" data-action="decrease" data-variant-id="${item.variant_id}">-</button>
                <input type="text" pattern="\d*" class="quote-cart-qty" value="${item.quantity}" min="1" data-variant-id="${item.variant_id}" />
                <button type="button" class="quote-qty-increment" data-action="increase" data-variant-id="${item.variant_id}">+</button>
              </div>
            </div>
          </div>
          <div class="quote-product-control">
            <button class="remove-quote-item" data-action="remove" data-variant-id="${item.variant_id}">Remove</button>
            <h3 class="item-total-price">${totalDisplay}</h3>
          </div>
        </div>
      </div>
    `;
  }

  setupDelegatedEventListeners() {
    // Single delegated event listener for all cart interactions
    document.addEventListener('click', (e) => {
      const target = e.target;
      const action = target.dataset.action;
      const variantId = parseInt(target.dataset.variantId);

      if (!action || !variantId) return;

      switch (action) {
        case 'increase':
          const currentQty = this.cart.find(item => item.variant_id === variantId)?.quantity || 1;
          this.updateQuantity(variantId, currentQty + 1);
          break;
        case 'decrease':
          const currentQtyDec = this.cart.find(item => item.variant_id === variantId)?.quantity || 1;
          if (currentQtyDec > 1) {
            this.updateQuantity(variantId, currentQtyDec - 1);
          }
          break;
        case 'remove':
          this.removeFromCart(variantId);
          break;
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.target.classList.contains('quote-cart-qty')) {
        const input = event.target;
        const isDigit = /^[0-9]$/.test(event.key);
        const isAllowedKey = ['Backspace', 'Delete', 'Tab', 'Escape', 'ArrowRight', 'ArrowLeft'].includes(event.key);
        if (event.key === 'Enter') {
          input.blur();
        } else if (!isDigit && !isAllowedKey) {
          event.preventDefault();
        }
      }
    });

    document.addEventListener('blur', (e) => {
      if (e.target.classList.contains('quote-cart-qty')) {
        const input = e.target;
        let newQuantity = parseInt(input.value);
        
        if (isNaN(newQuantity) || newQuantity < 1) {
          newQuantity = 1;
          input.value = 1;
        }
        
        const variantId = parseInt(input.dataset.variantId);
        this.updateQuantity(variantId, newQuantity);
      }
    }, true);

    document.addEventListener('click', (e) => {
      if (e.target.id === 'open-quote-modal') {
        this.handleQuoteButtonClick(e);
      }
    });

    document.addEventListener('click', (e) => {
      const button = e.target.closest('button[data-quote-request="true"]');
      if (!button) return;

      e.preventDefault();

      let productCollections = [];
      try {
        productCollections = JSON.parse(button.dataset.productCollections || '[]');
      } catch (error) {
        console.warn('Failed to parse product collections from button dataset:', error);
      }

      const productType = this.normalizeProductType(button.dataset.productType);

      window.handleCardQuoteRequest(
        Number(button.dataset.productId),
        button.dataset.productTitle || '',
        button.dataset.productImage || '',
        button.dataset.productHandle || '',
        productType,
        productCollections
      );
    });
  }

  handleQuoteButtonClick(e) {
    const container = document.getElementById('quote-product-container');
    if (!container) return;

    const variants = JSON.parse(container.dataset.variants || '[]');
    const selectors = document.querySelectorAll('.single-option-selector');
    
    const selectedOptions = Array.from(selectors).map(sel => sel.value);
    const variant = variants.find(variant =>
      selectedOptions.every((opt, i) => variant[`option${i + 1}`] === opt)
    );

    if (!variant) return;
    let productCollections = [];
    try {
      productCollections = JSON.parse(container.dataset.collections || '[]');
    } catch (error) {
      console.warn('Failed to parse PDP product collections:', error);
    }
    const productType = this.normalizeProductType(container.dataset.productType);
    const quoteItem = {
      product_title: container.dataset.title,
      product_id: container.dataset.id,
      variant_id: variant.id,
      price: variant.price,
      formatted_price: (variant.price / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
      sku: variant.sku || "No SKU",
      image: variant.featured_image?.src || container.dataset.image,
      quantity: 1,
      available: variant.available,
      product_type: productType,
      collections: Array.isArray(productCollections) ? productCollections : []
    };

    console.log('[Quote] Product type added to sessionStorage item:', quoteItem.product_type || '(blank)');

    const added = this.addToCart(quoteItem);
    if (added) {
      // Show popup
      window.dispatchEvent(new CustomEvent('showQuotePopup', { detail: quoteItem }));
    }
  }

  // Public methods for external use
  getCart() {
    return [...this.cart]; // Return copy to prevent external mutation
  }

  clearCart() {
    this.cart = [];
    this.saveCart();
    this.updateCartDisplay();
  }
}

// Initialize the quote cart manager
window.quoteCartManager = new QuoteCartManager();

// Export for backward compatibility
window.refreshQuoteCartDisplay = () => {
  window.quoteCartManager.updateCartDisplay();
};

function normalizeProductType(value) {
  return String(value || '').trim();
}

// Global handler for "Add to Quote" buttons on product cards.
// Defined here (not inline in the snippet) so it survives Globo Filter AJAX reloads.
window.handleCardQuoteRequest = function(productId, productTitle, productImage, productHandle, productType = '', productCollections = []) {
  fetch(`/products/${productHandle}.js`)
    .then(response => response.json())
    .then(product => {
      let firstAvailableVariant = product.variants.find(v => v.available);
      if (!firstAvailableVariant && product.variants.length > 0) {
        firstAvailableVariant = product.variants[0];
      }

      if (!firstAvailableVariant) {
        alert('No available variants for this product');
        return;
      }

      const quoteItem = {
        product_title: productTitle,
        product_id: productId,
        variant_id: firstAvailableVariant.id,
        price: firstAvailableVariant.price,
        formatted_price: (firstAvailableVariant.price / 100).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        sku: firstAvailableVariant.sku || "No SKU",
        image: firstAvailableVariant.featured_image?.src || (productImage.startsWith('//') ? `https:${productImage}` : productImage),
        variant_name: firstAvailableVariant.title,
        quantity: 1,
        available: firstAvailableVariant.available,
        product_type: normalizeProductType(productType || product.type),
        collections: Array.isArray(productCollections) ? productCollections : []
      };

      console.log('[Quote] Product type added to sessionStorage item:', quoteItem.product_type || '(blank)');

      if (window.quoteCartManager) {
        const added = window.quoteCartManager.addToCart(quoteItem);
        if (added) {
          window.dispatchEvent(new CustomEvent("showQuotePopup", { detail: quoteItem }));
        } else {
          const existingItem = window.quoteCartManager.getCart().find(item => item.variant_id === quoteItem.variant_id);
          if (existingItem) {
            window.quoteCartManager.updateQuantity(quoteItem.variant_id, existingItem.quantity + 1);
            window.dispatchEvent(new CustomEvent("showQuotePopup", { detail: quoteItem }));
          }
        }
      } else {
        let cart = [];
        try { cart = JSON.parse(sessionStorage.getItem("quoteCart")) || []; } catch (e) {}
        const existingIndex = cart.findIndex(item => item.variant_id === quoteItem.variant_id);
        if (existingIndex !== -1) {
          cart[existingIndex].quantity += 1;
        } else {
          cart.push(quoteItem);
        }
        sessionStorage.setItem("quoteCart", JSON.stringify(cart));
        window.dispatchEvent(new Event("quoteCartUpdated"));
        window.dispatchEvent(new CustomEvent("showQuotePopup", { detail: quoteItem }));
      }
    })
    .catch(error => {
      console.error('Error fetching product data:', error);
      alert('Error adding product to quote request');
    });
};
